import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { useCart } from '../contexts/CartContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Users, BookOpen, ArrowRight, ChevronLeft, ChevronRight, ShoppingCart } from 'lucide-react';

function useCountUp(target, duration = 1500) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    if (!target || started.current) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        const step = Math.ceil(target / (duration / 16));
        let current = 0;
        const timer = setInterval(() => {
          current += step;
          if (current >= target) { setCount(target); clearInterval(timer); }
          else setCount(current);
        }, 16);
      }
    }, { threshold: 0.3 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target, duration]);

  return { count, ref };
}

const DEFAULT_BANNERS = [
  'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=1200&h=500&fit=crop',
  'https://images.unsplash.com/photo-1523050854058-8df90110c5f1?w=1200&h=500&fit=crop',
  'https://images.unsplash.com/photo-1513258496099-48168024aec0?w=1200&h=500&fit=crop'
];

export default function HomePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const categoryFilter = searchParams.get('category');
  const { addItem, isInCart } = useCart();
  const [stats, setStats] = useState({ total_students: 0, total_test_series: 0 });
  const [series, setSeries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [siteSettings, setSiteSettings] = useState(null);
  const studentsCounter = useCountUp(stats.total_students);
  const seriesCounter = useCountUp(stats.total_test_series);

  const bannerImages = siteSettings?.banner_images?.length > 0 ? siteSettings.banner_images : DEFAULT_BANNERS;
  const bannerTitle = siteSettings?.banner_title || 'Start Practicing and achieve your goals.';
  const bannerSubtitle = siteSettings?.banner_subtitle || 'Practice, Analyze and Practice Again to be Arjun of your exam.';

  useEffect(() => {
    api.get('/stats').then(r => setStats(r.data)).catch(() => {});
    api.get('/categories').then(r => setCategories(r.data)).catch(() => {});
    api.get('/settings/public').then(r => setSiteSettings(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const params = categoryFilter ? `?category_id=${categoryFilter}&limit=50` : '?limit=50';
    api.get(`/test-series${params}`).then(r => setSeries(r.data.items || [])).catch(() => {});
  }, [categoryFilter]);

  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(() => setCurrentSlide(p => (p + 1) % bannerImages.length), 4000);
    return () => clearInterval(timer);
  }, [isPaused]);

  return (
    <main className="flex-1" data-testid="homepage">
      {/* Hero Section */}
      <section
        className="relative overflow-hidden bg-white"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        data-testid="hero-section"
      >
        <div className="max-w-7xl mx-auto px-4 py-12 lg:py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 animate-fade-in-up">
              <Badge className="bg-[#F4F0FF] text-[#7551e8] hover:bg-[#F4F0FF] border-0 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em]">
                India's Top Test Platform
              </Badge>
              <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-semibold text-[#0A0A0A] leading-[1.1]" data-testid="hero-headline">
                {bannerTitle}
              </h1>
              <p className="text-base lg:text-lg text-[#52525B] leading-relaxed max-w-lg" data-testid="hero-subtext">
                {bannerSubtitle}
              </p>
              <div className="flex flex-wrap gap-3">
                <Button
                  className="bg-[#7551e8] hover:bg-[#5e3dc7] text-white px-8 py-6 text-base font-medium rounded-lg"
                  onClick={() => navigate('/register')}
                  data-testid="hero-cta-btn"
                >
                  Get Started <ArrowRight size={18} className="ml-2" />
                </Button>
                <Button
                  variant="outline"
                  className="px-8 py-6 text-base font-medium rounded-lg border-zinc-300 hover:bg-zinc-50"
                  onClick={() => {
                    const el = document.getElementById('test-series-section');
                    el?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  data-testid="hero-explore-btn"
                >
                  Explore Tests
                </Button>
              </div>
            </div>

            {/* Carousel */}
            <div className="relative rounded-2xl overflow-hidden aspect-[4/3] bg-zinc-100" data-testid="hero-carousel">
              {bannerImages.map((img, i) => (
                <div key={i} className="absolute inset-0 transition-opacity duration-700" style={{ opacity: currentSlide === i ? 1 : 0 }}>
                  <img src={img} alt={`Banner ${i + 1}`} className="w-full h-full object-cover" />
                </div>
              ))}
              <button onClick={() => setCurrentSlide(p => (p - 1 + bannerImages.length) % bannerImages.length)}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/80 flex items-center justify-center hover:bg-white transition-colors shadow" data-testid="carousel-prev">
                <ChevronLeft size={18} />
              </button>
              <button onClick={() => setCurrentSlide(p => (p + 1) % bannerImages.length)}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/80 flex items-center justify-center hover:bg-white transition-colors shadow" data-testid="carousel-next">
                <ChevronRight size={18} />
              </button>
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                {bannerImages.map((_, i) => (
                  <button key={i} onClick={() => setCurrentSlide(i)}
                    className={`h-1.5 rounded-full transition-all ${currentSlide === i ? 'w-8 bg-[#7551e8]' : 'w-4 bg-white/60'}`} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-[#F9FAFB] border-y border-zinc-200" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-2xl mx-auto">
            <Card ref={studentsCounter.ref} className="p-6 bg-white border border-zinc-200 hover:shadow-md transition-shadow" data-testid="stat-students">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#7551e8] mb-1">Total Students Registered</p>
                  <p className="font-['Outfit'] text-4xl font-semibold text-[#0A0A0A]">{studentsCounter.count.toLocaleString()}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-[#F4F0FF] flex items-center justify-center">
                  <Users size={22} className="text-[#7551e8]" />
                </div>
              </div>
            </Card>
            <Card ref={seriesCounter.ref} className="p-6 bg-white border border-zinc-200 hover:shadow-md transition-shadow" data-testid="stat-series">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#7551e8] mb-1">Total Test Series Published</p>
                  <p className="font-['Outfit'] text-4xl font-semibold text-[#0A0A0A]">{seriesCounter.count.toLocaleString()}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-[#F4F0FF] flex items-center justify-center">
                  <BookOpen size={22} className="text-[#7551e8]" />
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* About Astha Foundation */}
      {(siteSettings?.foundation_logo_url || siteSettings?.founder_image_url) && (
        <section className="bg-white border-b border-zinc-200" data-testid="foundation-section">
          <div className="max-w-7xl mx-auto px-4 py-12 lg:py-16">
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div className="flex flex-col items-center lg:items-start gap-6">
                {siteSettings.foundation_logo_url && (
                  <img src={siteSettings.foundation_logo_url} alt="Astha Foundation" className="h-28 sm:h-36 w-auto object-contain" data-testid="foundation-logo" />
                )}
                <div className="text-center lg:text-left">
                  <h2 className="font-['Outfit'] text-2xl sm:text-3xl font-semibold text-[#0A0A0A] mb-3">An Initiative by Astha Foundation</h2>
                  <p className="text-[#52525B] leading-relaxed max-w-lg">Trusted since 2012, Astha Foundation is dedicated to empowering students with quality education and accessible learning resources for competitive exam preparation across India.</p>
                </div>
              </div>
              {siteSettings.founder_image_url && (
                <div className="flex flex-col items-center gap-4">
                  <div className="w-48 h-48 sm:w-56 sm:h-56 rounded-full overflow-hidden border-4 border-[#F4F0FF] shadow-lg">
                    <img src={siteSettings.founder_image_url} alt="Founder" className="w-full h-full object-cover" data-testid="founder-image" />
                  </div>
                  <div className="text-center">
                    <p className="font-['Outfit'] font-semibold text-lg text-[#0A0A0A]">Founder</p>
                    <p className="text-sm text-[#7551e8]">Astha Foundation</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Category Filter */}
      {categories.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 pt-12">
          <div className="flex flex-wrap gap-2">
            <Button
              variant={!categoryFilter ? "default" : "outline"}
              size="sm"
              className={!categoryFilter ? "bg-[#7551e8] hover:bg-[#5e3dc7] text-white" : ""}
              onClick={() => navigate('/')}
              data-testid="filter-all"
            >All</Button>
            {categories.map(cat => (
              <Button
                key={cat.id}
                variant={categoryFilter === cat.id ? "default" : "outline"}
                size="sm"
                className={categoryFilter === cat.id ? "bg-[#7551e8] hover:bg-[#5e3dc7] text-white" : ""}
                onClick={() => navigate(`/?category=${cat.id}`)}
                data-testid={`filter-${cat.name}`}
              >{cat.name}</Button>
            ))}
          </div>
        </section>
      )}

      {/* Test Series Grid */}
      <section id="test-series-section" className="max-w-7xl mx-auto px-4 py-12" data-testid="test-series-section">
        <h2 className="font-['Outfit'] text-2xl sm:text-3xl font-medium text-[#0A0A0A] mb-8">Popular Test Series</h2>
        {series.length === 0 ? (
          <div className="text-center py-16 text-[#52525B]">
            <BookOpen size={48} className="mx-auto mb-4 text-zinc-300" />
            <p className="text-lg">No test series available yet.</p>
            <p className="text-sm mt-1">Check back soon for new content!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {series.map((s, i) => (
              <Card key={s.id} className={`card-hover border border-zinc-200 overflow-hidden animate-fade-in-up stagger-${Math.min(i + 1, 5)}`} data-testid={`series-card-${s.id}`}>
                <div className="aspect-video bg-zinc-100 overflow-hidden">
                  <img
                    src={s.thumbnail_url || 'https://images.pexels.com/photos/7683899/pexels-photo-7683899.jpeg?auto=compress&w=400&h=250&fit=crop'}
                    alt={s.title}
                    className="w-full h-full object-cover"
                    onError={e => { e.target.src = 'https://images.pexels.com/photos/7683899/pexels-photo-7683899.jpeg?auto=compress&w=400&h=250&fit=crop'; }}
                  />
                </div>
                <div className="p-4 space-y-3">
                  <h3 className="font-['Outfit'] font-medium text-[#0A0A0A] line-clamp-2">{s.title}</h3>
                  <div className="flex items-center gap-2 text-xs text-[#52525B]">
                    <BookOpen size={12} />
                    <span>{s.total_tests || 0} Tests</span>
                    {s.validity_months && <span className="ml-auto">{s.validity_months} months</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    {s.discount_price > 0 && s.discount_price < s.price ? (
                      <>
                        <span className="font-['Outfit'] font-semibold text-lg text-[#0A0A0A]">&#8377;{s.discount_price}</span>
                        <span className="price-original text-sm">&#8377;{s.price}</span>
                        <Badge className="bg-green-50 text-green-700 border-0 text-xs ml-auto">
                          {Math.round((1 - s.discount_price / s.price) * 100)}% OFF
                        </Badge>
                      </>
                    ) : s.price > 0 ? (
                      <span className="font-['Outfit'] font-semibold text-lg text-[#0A0A0A]">&#8377;{s.price}</span>
                    ) : (
                      <span className="font-['Outfit'] font-semibold text-lg text-[#10B981]">Free</span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      className="flex-1 bg-[#7551e8] hover:bg-[#5e3dc7] text-white"
                      onClick={() => navigate(`/test-series/${s.id}`)}
                      data-testid={`view-series-${s.id}`}
                    >
                      View Details
                    </Button>
                    {s.price > 0 && (
                      <Button
                        variant="outline"
                        className={isInCart(s.id) ? "border-[#7551e8] text-[#7551e8] shrink-0" : "shrink-0"}
                        onClick={() => { if (!isInCart(s.id)) addItem(s); }}
                        disabled={isInCart(s.id)}
                        data-testid={`add-cart-${s.id}`}
                      >
                        <ShoppingCart size={14} className="mr-1" />
                        {isInCart(s.id) ? 'In Cart' : 'Add to Cart'}
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
